import Axhelper from '@/lib/Axhelper'

class CertificationsService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG?.API_URL || ''
		this.endpoints = {
			certificationsAll: '/api/certyfikaty/all',
			certificationsDetails: '/api/certyfikaty/details',
			certificationsSearch: '/api/certyfikaty/search',
			certificationsFilters: '/api/certyfikaty/filters',
			certificationsCreate: '/api/certyfikaty',
			certificationsUpdate: '/api/certyfikaty',
			certificationsDelete: '/api/certyfikaty',
		}
	}

	async getAll(options = {}) {
		try {
			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = this._buildParams({ limit, sortColumn, sortDirection, filters })
			const url = `${this.endpoints.certificationsAll}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			return this._buildListResponse(response)
		} catch (error) {
			console.error('CertificationsService.getAll error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch certifications',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getById(certificationId) {
		try {
			if (!certificationId) {
				return {
					data: null,
					error: 'Invalid certification ID provided',
				}
			}

			const url = `${this.endpoints.certificationsDetails}/${encodeURIComponent(certificationId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: null,
					error: response.error,
				}
			}

			return {
				data: this._normalizeCertification(response.data),
				error: null,
			}
		} catch (error) {
			console.error('CertificationsService.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch certification',
			}
		}
	}

	async search(searchTerm, options = {}) {
		try {
			const trimmed = `${searchTerm || ''}`.trim()
			if (!trimmed) {
				return {
					data: [],
					error: 'Invalid search term provided',
					totalCount: 0,
					hasMore: false,
				}
			}

			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = this._buildParams({ limit, sortColumn, sortDirection, filters })
			const url = `${this.endpoints.certificationsSearch}/${encodeURIComponent(trimmed)}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			return this._buildListResponse(response)
		} catch (error) {
			console.error('CertificationsService.search error:', error)
			return {
				data: [],
				error: error.message || 'Failed to search certifications',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getWithFilters(options = {}) {
		try {
			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = this._buildParams({ limit, sortColumn, sortDirection, filters })
			const searchText = `${filters?.searchText || ''}`.trim()
			if (searchText) {
				params.append('searchText', searchText)
			}

			const url = `${this.endpoints.certificationsFilters}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			return this._buildListResponse(response)
		} catch (error) {
			console.error('CertificationsService.getWithFilters error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch certifications',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async create(certificationData) {
		try {
			const response = await Axhelper.sendPostAsync(
				this.endpoints.certificationsCreate,
				this._buildFormData(certificationData)
			)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					data: null,
				}
			}

			return {
				success: true,
				error: null,
				data: this._normalizeCertification(response.data),
			}
		} catch (error) {
			console.error('CertificationsService.create error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create certification',
				data: null,
			}
		}
	}

	async update(certificationId, certificationData) {
		try {
			if (!certificationId) {
				return {
					success: false,
					error: 'Invalid certification ID provided',
					data: null,
				}
			}

			const url = `${this.endpoints.certificationsUpdate}/${encodeURIComponent(certificationId)}`
			const response = await Axhelper.sendPutAsync(url, this._buildFormData(certificationData))

			if (response.error) {
				return {
					success: false,
					error: response.error,
					data: null,
				}
			}

			return {
				success: true,
				error: null,
				data: this._normalizeCertification(response.data),
			}
		} catch (error) {
			console.error('CertificationsService.update error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update certification',
				data: null,
			}
		}
	}

	async delete(certificationId) {
		try {
			if (!certificationId) {
				return {
					success: false,
					error: 'Invalid certification ID provided',
					message: null,
				}
			}

			const url = `${this.endpoints.certificationsDelete}/${encodeURIComponent(certificationId)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					message: null,
				}
			}

			return {
				success: true,
				error: null,
				message: response.data || 'Certification deleted successfully',
			}
		} catch (error) {
			console.error('CertificationsService.delete error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete certification',
				message: null,
			}
		}
	}

	_buildParams({ limit, sortColumn, sortDirection, filters }) {
		const params = new URLSearchParams({
			sortcol: this._mapSortColumn(sortColumn),
			sortdir: sortDirection,
			limit: String(limit),
		})

		Object.entries(filters || {}).forEach(([key, value]) => {
			if (key === 'searchText' || value === undefined || value === null || value === '') return
			params.append(key, String(value))
		})

		return params
	}

	_buildFormData(data = {}) {
		const params = new URLSearchParams()

		params.append('nazwa', data.nazwa ?? data.name ?? '')
		params.append('opis', data.opis ?? data.description ?? '')

		return params
	}

	_buildListResponse(response) {
		const rows = this._normalizeCertifications(response.data)
		const totalCount = response.headers?.['x-total-count']
			? parseInt(response.headers['x-total-count'], 10)
			: rows.length

		return {
			data: rows,
			error: null,
			totalCount,
			hasMore: rows.length < totalCount,
			headers: response.headers,
		}
	}

	_normalizeCertifications(certifications = []) {
		return Array.isArray(certifications)
			? certifications.map(item => this._normalizeCertification(item)).filter(Boolean)
			: []
	}

	_normalizeCertification(certification) {
		if (!certification || typeof certification !== 'object') return null

		const certificationId = certification?._id || certification?.id || ''
		const name = certification?.nazwa ?? certification?.name ?? ''
		const description = certification?.opis ?? certification?.description ?? ''

		return {
			...certification,
			_id: certificationId,
			id: certification?.id || certificationId,
			name,
			description,
			nazwa: certification?.nazwa ?? name,
			opis: certification?.opis ?? description,
		}
	}

	_mapSortColumn(sortColumn) {
		const columnsMap = {
			name: 'nazwa',
			description: 'opis',
		}

		return columnsMap[sortColumn] || sortColumn || '_id'
	}
}

export default new CertificationsService()
